import { reactive } from 'vue';
import { ElLoading } from 'element-plus';

import util from '@/Utils/util';
import { paymentService } from '@/services/payment';
import { useSwalDialog } from '@/use/swalDialog';
import type { StripeIntentKind } from '@/use/stripe';

export type PaymentSourceCheckStatus = {
  setupIntentId: string;
  confirmationTokenId?: string;
  cardHolderType: string;
  warningMessage: string;
  /** Whether setupIntentId holds a SetupIntent ('setup') or registration PaymentIntent ('payment'). */
  intentKind: StripeIntentKind;
};

export const usePaymentSourceCheck = () => {
  const { showErrorPopupDialog, showConfirmPopupDialog } = useSwalDialog();

  const status = reactive<PaymentSourceCheckStatus>({
    setupIntentId: '',
    confirmationTokenId: '',
    cardHolderType: '',
    warningMessage: '',
    intentKind: 'setup',
  });

  const clearPaymentSourceCheck = () => {
    status.setupIntentId = '';
    status.confirmationTokenId = '';
    status.cardHolderType = '';
    status.warningMessage = '';
    status.intentKind = 'setup';
  };

  const showCheckPaymentSourceError = async (err: unknown): Promise<boolean> => {
    const errorCode = util.getGraphqlErrorCode(err);
    if (errorCode === 'noPrepaidCards') {
      let errorHtml = '<strong>Card Type</strong>: Prepaid<br />';
      errorHtml = `${errorHtml}<strong>Notice</strong>: We are unable to save prepaid cards to a customers account.<br />`;
      errorHtml = `${errorHtml}<strong>Solution</strong>: Add GoGo Credits with the prepaid card, on the GoGo Credits Tab.`;

      await showErrorPopupDialog({
        text: errorHtml,
        type: 'html',
      });
      return false;
    }

    const errorText = util.getGraphqlError(err, false);
    await showErrorPopupDialog({
      text: errorText,
      type: 'html',
    });
    return false;
  };

  const checkCreditCard = async ({
    setupIntentId,
    confirmationTokenId,
    intentKind = 'setup',
    userId,
    onCheckFailed,
    onAddAnyway,
    treatReturnedMessageAsError = false,
  }: {
    setupIntentId: string;
    confirmationTokenId?: string;
    intentKind?: StripeIntentKind;
    userId: string;
    onCheckFailed?: () => void;
    onAddAnyway?: () => void | Promise<void>;
    /** When true, a non-empty string from checkPaymentSource is shown and blocks adding the card. */
    treatReturnedMessageAsError?: boolean;
  }): Promise<boolean> => {
    status.warningMessage = '';

    const loading = ElLoading.service({
      lock: true,
      text: 'Checking Credit Card...',
      background: 'rgba(0, 0, 0, 0.7)',
      customClass: 'custom-full-spinner-class',
    });

    try {
      status.warningMessage = await paymentService.checkPaymentSource({
        setupIntentId,
        confirmationTokenId,
        intentKind,
        userId,
      });

      if (treatReturnedMessageAsError && status.warningMessage) {
        const addAnyway = await showConfirmPopupDialog({
          title: 'Error!',
          text: status.warningMessage,
          type: 'html',
          icon: 'error',
          confirmButtonText: 'Add anyway',
          cancelButtonText: 'OK',
        });

        if (addAnyway) {
          status.setupIntentId = setupIntentId;
          await onAddAnyway?.();
          return true;
        }

        status.setupIntentId = '';
        onCheckFailed?.();
        return false;
      }

      return true;
    } catch (err) {
      await showCheckPaymentSourceError(err);
      clearPaymentSourceCheck();
      onCheckFailed?.();
      return false;
    } finally {
      loading.close();
    }
  };

  return {
    status,
    clearPaymentSourceCheck,
    checkCreditCard,
    showCheckPaymentSourceError,
  };
};
